import type { ColumnVisibilityState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Actions } from '../../../../components/product-table/columns/actions-column';
import { Bin } from '../../../../components/product-table/columns/bin-column';
import { CaseQty } from '../../../../components/product-table/columns/case-qty-column';
import { CurrentCost } from '../../../../components/product-table/columns/current-cost-column';
import { DOH } from '../../../../components/product-table/columns/doh-column';
import { FutureCost } from '../../../../components/product-table/columns/future-cost-column';
import { ItemCode } from '../../../../components/product-table/columns/item-code-column';
import { LastCaseCost } from '../../../../components/product-table/columns/last-case-cost-column';
import { LastCost } from '../../../../components/product-table/columns/last-cost-column';
import { lineNumberColumn } from '../../../../components/product-table/columns/line-number-column';
import { MarginDollars } from '../../../../components/product-table/columns/margin-dollars-column';
import { MarginPercentage } from '../../../../components/product-table/columns/margin-percentage-column';
import { Price } from '../../../../components/product-table/columns/price-column';
import { QOHCases } from '../../../../components/product-table/columns/qoh-cases-column';
import { QOH } from '../../../../components/product-table/columns/qoh-column';
import {
  getCasesSoldColumn,
  getUnitsSoldColumn,
} from '../../../../components/product-table/columns/sales-columns';
import { SavingsDollars } from '../../../../components/product-table/columns/savings-dollars-column';
import {
  DealsSavingsIndicator,
  VolumeSavingsIndicator,
} from '../../../../components/product-table/columns/savings-indicator-column';
import { SavingsPercentage } from '../../../../components/product-table/columns/savings-percentage-column';
import {
  Department,
  OrderLot,
  StdQty,
  Type,
} from '../../../../components/product-table/columns/simple-columns';
import { Size } from '../../../../components/product-table/columns/size-column';
import { remapColumn, type StrictColumnDef } from '../../../../components/tanstack-table/column';
import { isPivotEligible } from '../../../../data/product';
import { hasSwingDeals } from '../../../../data/states';
import { useVisibleColumns } from '../../../../hooks/use-visible-columns';
import { AnnualProfit } from '../components/annual-profit-column';
import { makeCountColumn } from '../components/count-column';
import { extendedBuyupCostColumn } from '../components/extended-buyup-cost-column';
import { extendedCostColumn } from '../components/extended-cost-column';
import { OpportunityCost } from '../components/opportunity-cost-column';
import { OutOfStockRisk } from '../components/out-of-stock-risk-column';
import { makeProductPivotColumn } from '../components/product-pivot-column';
import { SavingsPotential90d } from '../components/savings-potential-column';
import { swingSavingsColumn } from '../components/swing-savings-column';
import type { ProductOrderItem } from '../data/product-order-item';
import type { ColumnOptions } from '../types';
import { toProduct } from '../utils';

export const BUYUP_COLUMNS_KEY = 'order-buyup-columns';
export const ORDER_COLUMNS_KEY = 'order-columns';

export const DEFAULT_HIDDEN_COLUMNS = [
  OpportunityCost.id,
  AnnualProfit.id,
  OutOfStockRisk.id,
  Bin.id,
];

export const PINNED_COLUMNS = ['Product'];

type ColumnsResult = {
  /** All columns (including hidden) — pass to TanStackTable for sorting */
  columns: StrictColumnDef<ProductOrderItem>[];
  /** Only visible columns — use for layout/sizing outside the table */
  visibleColumns: StrictColumnDef<ProductOrderItem>[];
  /** TanStack visibility state — pass to TanStackTable */
  columnVisibility: ColumnVisibilityState;
  /** TanStack columnOrder state */
  columnOrder: string[];
};

function useBuyupColumns({
  store,
  setOrderItem,
  reorderColumn,
  onCasesSoldClick,
  onBrandPivot,
}: ColumnOptions): ColumnsResult {
  const allColumns = useMemo(() => {
    const isSwingDeals = hasSwingDeals(store);

    return [
      lineNumberColumn<ProductOrderItem>(),
      remapColumn(Bin, toProduct),
      remapColumn(ItemCode, toProduct),
      makeProductPivotColumn({
        onPivot: onBrandPivot,
        isEligible: () => true,
        tooltipText: (product) => `Filter to ${product.brand}`,
        affordance: 'hover-only',
      }),
      remapColumn(Size, toProduct),
      remapColumn(CaseQty, toProduct),
      remapColumn(CurrentCost, toProduct),
      isSwingDeals && remapColumn(FutureCost, toProduct),
      isSwingDeals && remapColumn(SavingsDollars, toProduct),
      isSwingDeals && remapColumn(SavingsPercentage, toProduct),
      remapColumn(DOH, toProduct),
      remapColumn(QOHCases, toProduct),
      remapColumn(getCasesSoldColumn(30, onCasesSoldClick), toProduct),
      remapColumn(getCasesSoldColumn(60, onCasesSoldClick), toProduct),
      remapColumn(getCasesSoldColumn(90, onCasesSoldClick), toProduct),
      remapColumn(getCasesSoldColumn(180, onCasesSoldClick), toProduct),
      remapColumn(getCasesSoldColumn(365, onCasesSoldClick), toProduct),
      makeCountColumn(setOrderItem, 'cases'),
      extendedBuyupCostColumn,
      reorderColumn,
      isSwingDeals && swingSavingsColumn,
      SavingsPotential90d,
      isSwingDeals && remapColumn(DealsSavingsIndicator, toProduct),
      AnnualProfit,
      OutOfStockRisk,
      OpportunityCost,
      remapColumn(Actions, toProduct),
    ].filter((col): col is StrictColumnDef<ProductOrderItem> => Boolean(col));
  }, [store, setOrderItem, reorderColumn, onCasesSoldClick, onBrandPivot]);

  return useVisibleColumns(allColumns, BUYUP_COLUMNS_KEY, {
    defaultHiddenColumnIds: DEFAULT_HIDDEN_COLUMNS,
  });
}

function useOrderColumns({
  store,
  vendorHasVolume,
  setOrderItem,
  reorderColumn,
  showsSavingsIndicators,
  displayInCases,
  onPivotToBuyup,
}: ColumnOptions): ColumnsResult {
  const allColumns = useMemo(() => {
    const qohColum = displayInCases ? QOHCases : QOH;
    const salesColumn = displayInCases ? getCasesSoldColumn(30) : getUnitsSoldColumn(30);
    const costColumn = displayInCases ? LastCaseCost : LastCost;

    const columns = [
      remapColumn(Bin, toProduct),
      remapColumn(ItemCode, toProduct),
      makeProductPivotColumn({
        onPivot: onPivotToBuyup,
        isEligible: (product) => isPivotEligible(product, vendorHasVolume),
        tooltipText: (product) => `Jump to ${product.brand} buyup deals`,
        shortcutHint: 'meta+click',
      }),
      remapColumn(Department, toProduct),
      remapColumn(Type, toProduct),
      remapColumn(Size, toProduct),
      remapColumn(CaseQty, toProduct),
      remapColumn(StdQty, toProduct),
      remapColumn(OrderLot, toProduct),
      remapColumn(DOH, toProduct),
      remapColumn(qohColum, toProduct),
      remapColumn(salesColumn, toProduct),
      makeCountColumn(setOrderItem, 'cases'),
      makeCountColumn(setOrderItem, 'units'),
      remapColumn(costColumn, toProduct),
      extendedCostColumn,
      reorderColumn,
      remapColumn(Price, toProduct),
      remapColumn(MarginPercentage, toProduct),
      remapColumn(MarginDollars, toProduct),
      AnnualProfit,
      OutOfStockRisk,
      OpportunityCost,
    ];
    if (showsSavingsIndicators && vendorHasVolume) {
      columns.push(remapColumn(VolumeSavingsIndicator, toProduct));
    }

    if (showsSavingsIndicators && hasSwingDeals(store)) {
      columns.push(remapColumn(DealsSavingsIndicator, toProduct));
    }

    columns.push(remapColumn(Actions, toProduct));

    return columns;
  }, [
    setOrderItem,
    reorderColumn,
    showsSavingsIndicators,
    displayInCases,
    store,
    vendorHasVolume,
    onPivotToBuyup,
  ]);

  return useVisibleColumns(allColumns, ORDER_COLUMNS_KEY, {
    defaultHiddenColumnIds: DEFAULT_HIDDEN_COLUMNS,
  });
}

export function useColumns(columnOptions: ColumnOptions): ColumnsResult {
  const buyupResult = useBuyupColumns(columnOptions);
  const orderResult = useOrderColumns(columnOptions);

  if (columnOptions.isBuyup) {
    return buyupResult;
  }

  return orderResult;
}
